// Hand-tuned starter compositions. Each ships forever — DSL must continue to
// load through every future schema migration (see migrate.ts).

export type Preset = {
  id: string;
  name: string;
  tags: readonly string[];
  dsl: string;
};

export const PRESETS: readonly Preset[] = [
  {
    id: 'noise-storm',
    name: 'Noise Storm',
    tags: ['noise', 'topographic', 'green'],
    dsl: `@128x128 #v1
perlin(a=#1a1c2c, b=#a7f070, contrast=1.4, octaves=5, persistence=0.5, scale=24, seed=7)
palette(palette=sweetie-16)`,
  },
  {
    id: 'voronoi-isles',
    name: 'Voronoi Isles',
    tags: ['voronoi', 'mosaic', 'gameboy'],
    dsl: `@128x128 #v1
voronoi(edges=false, hue=200, sites=8, spread=80, seed=11)
dither(algo=bayer8, amount=1, levels=4)
palette(palette=kirokaze-gb)`,
  },
  {
    id: 'plasma-1bit',
    name: 'Plasma 1-bit',
    tags: ['noise', 'mono', '1bit'],
    dsl: `@128x128 #v1
perlin(a=#000000, b=#ffffff, contrast=2, octaves=3, persistence=0.6, scale=18, seed=42)
dither(algo=floyd, amount=1, levels=2)
palette(palette=mono)`,
  },
  {
    id: 'lava-cells',
    name: 'Lava Cells',
    tags: ['voronoi', 'warm'],
    dsl: `@128x128 #v1
voronoi(edges=true, edgeColor=#1a0a05, hue=20, sites=24, spread=40, seed=3)
palette(palette=lava-gb)`,
  },
  {
    id: 'orange-grid',
    name: 'Orange Grid',
    tags: ['lattice', 'minimal'],
    dsl: `@128x128 #v1
solid(color=#1a0a05)
lattice(a=#ff5a1f, b=#1a0a05, shape=square, size=12, thickness=2)
palette(palette=duotone-orange)`,
  },
  {
    id: 'icecream-rings',
    name: 'Ice Cream Rings',
    tags: ['gradient', 'soft'],
    dsl: `@128x128 #v1
gradient(a=#7c3f58, angle=0, b=#fff6d3, power=1.2, type=radial)
dither(algo=bayer4, amount=1, levels=4)
palette(palette=ice-cream-gb)`,
  },
  {
    id: 'oil-radial',
    name: 'Oil Radial',
    tags: ['gradient', 'painterly'],
    dsl: `@128x128 #v1
gradient(a=#272744, angle=45, b=#fbf5ef, power=0.8, type=conic, seed=0)
posterize(levels=6)
palette(palette=oil-6)`,
  },
  {
    id: 'cells-mono',
    name: 'Cells Mono',
    tags: ['cellular', 'animated', 'mono'],
    dsl: `@128x128 #v1
cellular(alive=#ededed, dead=#0a0a0a, density=0.32, seed=5)
palette(palette=mono)`,
  },
  {
    id: 'kaleido-perlin',
    name: 'Kaleido Perlin',
    tags: ['noise', 'symmetry'],
    dsl: `@128x128 #v1
perlin(a=#272744, b=#ee8695, contrast=1.4, octaves=4, persistence=0.55, scale=40, seed=99)
symmetry(mode=kaleido, source=left)
palette(palette=twilight-5)`,
  },
  {
    id: 'halftone-cells',
    name: 'Halftone Cells',
    tags: ['voronoi', 'halftone'],
    dsl: `@128x128 #v1
voronoi(edges=false, hue=120, sites=12, spread=60, seed=15)
halftone(cell=8, ink=#000000, invert=false, paper=#ffffff, shape=dot)
palette(palette=mono)`,
  },
  {
    id: 'rust-noise',
    name: 'Rust Noise',
    tags: ['worley', 'warm'],
    dsl: `@128x128 #v1
worley(a=#202020, b=#f6cd26, invert=false, mode=f2-f1, sites=18, seed=20)
palette(palette=rust-gold-8)`,
  },
  {
    id: 'pico-storm',
    name: 'Pico Storm',
    tags: ['noise', 'pico-8'],
    dsl: `@128x128 #v1
perlin(a=#1d2b53, b=#ff004d, contrast=1.6, octaves=6, persistence=0.5, scale=22, seed=88)
dither(algo=bayer4, amount=1, levels=6)
palette(palette=pico-8)`,
  },

  // ── Landscapes ────────────────────────────────────────────────────────────
  {
    id: 'mountain-pass',
    name: 'Mountain Pass',
    tags: ['landscape', 'noise'],
    dsl: `@128x128 #v1
perlin(a=#272744, b=#fbf5ef, contrast=1.7, octaves=5, persistence=0.5, scale=42, seed=14)
posterize(levels=5)
palette(palette=oil-6)`,
  },
  {
    id: 'sea-foam',
    name: 'Sea Foam',
    tags: ['landscape', 'voronoi'],
    dsl: `@128x128 #v1
gradient(a=#29366f, angle=90, b=#73eff7, power=1, type=linear)
worley(a=#000000, b=#ffffff, invert=true, mode=f1, sites=40, seed=12) [overlay, 0.4]
palette(palette=sweetie-16)`,
  },
  {
    id: 'sunset-marsh',
    name: 'Sunset Marsh',
    tags: ['landscape', 'gradient'],
    dsl: `@128x128 #v1
gradient(a=#5d275d, angle=90, b=#ffcd75, power=1.4, type=linear)
perlin(a=#000000, b=#ffffff, contrast=1.2, octaves=4, persistence=0.55, scale=28, seed=33) [multiply, 0.5]
palette(palette=sweetie-16)`,
  },
  {
    id: 'aurora',
    name: 'Aurora',
    tags: ['landscape', 'symmetry'],
    dsl: `@128x128 #v1
gradient(a=#1a1c2c, angle=270, b=#a7f070, power=1.2, type=linear)
perlin(a=#000000, b=#41a6f6, contrast=1.5, octaves=5, persistence=0.5, scale=24, seed=77) [screen, 0.7]
symmetry(mode=mirror-h, source=left)
palette(palette=sweetie-16)`,
  },
  {
    id: 'coral-reef',
    name: 'Coral Reef',
    tags: ['landscape', 'voronoi'],
    dsl: `@128x128 #v1
voronoi(edges=true, edgeColor=#1a0a05, hue=12, sites=20, spread=80, seed=27)
halftone(cell=4, ink=#7a2d10, invert=false, paper=#ffe7d6, shape=dot) [multiply, 0.5]
palette(palette=pollen-8)`,
  },

  // ── Patterns ──────────────────────────────────────────────────────────────
  {
    id: 'diamond-lattice',
    name: 'Diamond Lattice',
    tags: ['pattern', 'lattice'],
    dsl: `@128x128 #v1
lattice(a=#0a0a0a, b=#ff5a1f, shape=triangle, size=18, thickness=1)
posterize(levels=2)
palette(palette=duotone-orange)`,
  },
  {
    id: 'hex-wave',
    name: 'Hex Wave',
    tags: ['pattern', 'lattice', 'gradient'],
    dsl: `@128x128 #v1
gradient(a=#1a1c2c, angle=45, b=#41a6f6, power=1, type=linear)
lattice(a=#000000, b=#ffffff, shape=hex, size=10, thickness=1) [multiply, 0.55]
palette(palette=sweetie-16)`,
  },
  {
    id: 'triangle-mesh',
    name: 'Triangle Mesh',
    tags: ['pattern', 'lattice', 'dither'],
    dsl: `@128x128 #v1
lattice(a=#332c50, b=#94e344, shape=triangle, size=12, thickness=1)
dither(algo=atkinson, amount=1, levels=3)
palette(palette=kirokaze-gb)`,
  },
  {
    id: 'stripe-storm',
    name: 'Stripe Storm',
    tags: ['pattern', 'lines'],
    dsl: `@128x128 #v1
lattice(a=#181425, b=#feae34, shape=lines, size=4, thickness=1)
perlin(a=#000000, b=#ffffff, contrast=1.4, octaves=3, persistence=0.5, scale=18, seed=2) [overlay, 0.6]
palette(palette=endesga-32)`,
  },

  // ── Abstracts ─────────────────────────────────────────────────────────────
  {
    id: 'ink-splash',
    name: 'Ink Splash',
    tags: ['abstract', 'worley', 'mono'],
    dsl: `@128x128 #v1
worley(a=#ffffff, b=#0a0a0a, invert=false, mode=f1, sites=12, seed=44)
threshold(high=#000000, low=#ffffff, threshold=0.45)
palette(palette=mono)`,
  },
  {
    id: 'plasma-field',
    name: 'Plasma Field',
    tags: ['abstract', 'noise', 'symmetry'],
    dsl: `@128x128 #v1
perlin(a=#fbbbad, b=#333f58, contrast=1.6, octaves=5, persistence=0.55, scale=28, seed=21)
symmetry(mode=radial-6, source=left)
palette(palette=twilight-5)`,
  },
  {
    id: 'static-burst',
    name: 'Static Burst',
    tags: ['abstract', 'noise', 'dither'],
    dsl: `@128x128 #v1
perlin(a=#000000, b=#ffffff, contrast=2.4, octaves=2, persistence=0.7, scale=12, seed=66)
dither(algo=floyd, amount=1, levels=2)
palette(palette=mono)`,
  },
  {
    id: 'neon-vortex',
    name: 'Neon Vortex',
    tags: ['abstract', 'gradient', 'noise'],
    dsl: `@128x128 #v1
gradient(a=#7e2553, angle=0, b=#ffec27, power=0.9, type=conic)
perlin(a=#000000, b=#ffffff, contrast=1.3, octaves=4, persistence=0.5, scale=20, seed=51) [multiply, 0.5]
palette(palette=pico-8)`,
  },
  {
    id: 'glitch-bars',
    name: 'Glitch Bars',
    tags: ['abstract', 'lines', 'threshold'],
    dsl: `@128x128 #v1
lattice(a=#000000, b=#ededed, shape=lines, size=6, thickness=2)
perlin(a=#ff5a1f, b=#000000, contrast=1.8, octaves=3, persistence=0.5, scale=10, seed=4) [screen, 0.5]
threshold(high=#ff5a1f, low=#0a0a0a, threshold=0.4)
palette(palette=duotone-orange)`,
  },

  // ── Animations ────────────────────────────────────────────────────────────
  {
    id: 'pulse-cells',
    name: 'Pulse Cells',
    tags: ['cellular', 'animated', 'orange'],
    dsl: `@96x96 #v1
cellular(alive=#ff5a1f, dead=#1a0a05, density=0.3, seed=12)
palette(palette=duotone-orange)`,
  },
  {
    id: 'drift-cells',
    name: 'Drift Cells',
    tags: ['cellular', 'animated', 'halftone'],
    dsl: `@96x96 #v1
cellular(alive=#fff6d3, dead=#7c3f58, density=0.42, seed=8)
halftone(cell=4, ink=#7c3f58, invert=false, paper=#fff6d3, shape=dot)
palette(palette=ice-cream-gb)`,
  },
  {
    id: 'glow-wave',
    name: 'Glow Wave',
    tags: ['cellular', 'animated', 'gradient'],
    dsl: `@96x96 #v1
gradient(a=#0a0a0a, angle=90, b=#41a6f6, power=1.2, type=radial)
cellular(alive=#ffec27, dead=#000000, density=0.36, seed=22) [screen, 0.7]
palette(palette=pico-8)`,
  },

  // ── Inks / Photo styles ───────────────────────────────────────────────────
  {
    id: 'risograph',
    name: 'Risograph',
    tags: ['halftone', 'duotone'],
    dsl: `@128x128 #v1
gradient(a=#ffffff, angle=45, b=#ff5a1f, power=1.4, type=radial)
halftone(cell=6, ink=#7a2d10, invert=false, paper=#ffe7d6, shape=dot)
palette(palette=duotone-orange)`,
  },
];

export function getPreset(id: string): Preset | undefined {
  return PRESETS.find((p) => p.id === id);
}
